import type { Dispatch, JSX } from 'react';
import {
    Alert,
    Box,
    Col,
    Divider,
    FormErrorMessage,
    FormGroup,
    RadioGroupWithTextInput,
    Row,
    Text,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { PatientInsuranceHistory } from '@common/interfaces/api/patientsApi';
import type { ITooDiagnosticReportCustomControlData } from '@common/interfaces/ophthalmology/TOO/tooDiagnosticReportCustomControlData';
import type { IAsset } from '@common/validation/assets';
import type {
    IOphthalmologyAsset,
    ITooProcedureCustomControlData,
} from '@common/validation/customControls/ophthalmology';
import type { ICustomControlProcedureAddEditProps } from '@frontend/components/pages/customControls/interfaces';
import type { TooV1ProcedureRequiredFields } from '@frontend/components/pages/customControls/TOO/v1/TooV1ProcedureDiagnosticReportAddEdit';
import type { ValidationErrors } from '@frontend/utils';
import { tooV1DiagnosticReportInitializeCustomControlData } from '@common/customControls/tooV1DiagnosticReportInitializeCustomControlData';
import { tooV1InitializeCustomControlData } from '@common/customControls/tooV1InitializeCustomControlData';
import { tooV1ProcedureInitializeCustomControlData } from '@common/customControls/tooV1ProcedureInitializeCustomControlData';
import { CustomControlMode, FilesUrlBase, ProcessEntity, ServiceCode, ToastType } from '@common/enums/';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { getUzoviRegisterItem } from '@common/uzoviRegister';
import {
    huisartsBetrokkenheid,
    OphthalmologyFilePlacement,
    TooProtocol,
    TooRedenBezoek,
} from '@common/validation/customControls/ophthalmology';
import { SaveButton, SaveSendButton } from '@frontend/components/elements/carePathButtons';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { SearchableEnumDropdown } from '@frontend/components/elements/SearchableEnumDropdown';
import { TextAreaField, TriStateField } from '@frontend/components/fields';
import { BooleanRadioGroupFormGroupField } from '@frontend/components/fields/BooleanRadioGroupField';
import {
    getFilesByPlacement,
    OphthalmologyDropzoneProcedureAddEdit,
} from '@frontend/components/pages/customControls/TFO';
import { TooExtraExaminationSelector } from '@frontend/components/pages/customControls/TOO/v1/TooExtraExaminationSelector';
import { TooV1ProcedureAddEditOdOsLabel } from '@frontend/components/pages/customControls/TOO/v1/TooV1ProcedureAddEditOdOsLabel';
import {
    AssetDropzoneWithActions,
    NewOrEditProcedureProcessContainer,
    ServiceOrganizationConsumerSelector,
} from '@frontend/components/pages/customControls/utils';
import { PatientInsuranceOldCarepath } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, getValidationErrorTranslation, renameErrorKeys } from '@frontend/utils';
import { createRadioGroupOptions } from '@frontend/utils/createRadioGroupOptions';
import { showServiceToast } from '@frontend/utils/showToast';
import {
    ButtonContainer,
    HeaderDivider,
    InputText,
    SmallNullableInputText,
    StyledBorderAndPadding,
} from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import {
    checkIsGeneralPractitionerInvolvementRequired,
    checkIsReferralCheckRequired,
    getIsReferralCheckCompleted,
    requestServiceConditionals,
} from './serviceConditionalsService';
import { validateReferralCheck } from './validateReferralCheck';

const huisartsBetrokkenheidOptions = [
    huisartsBetrokkenheid.doorlopendAfspraken,
    huisartsBetrokkenheid.mondelingeVerwijzing,
    huisartsBetrokkenheid.schriftelijkeVerwijzing,
];

const tooCylindrishFields = [
    'refractie_cylindrish_od',
    'refractie_cylindrish_os',
    'eigen_correctie_cylindrish_od',
    'eigen_correctie_cylindrish_os',
    'autorefractie_cylindrisch_od',
    'autorefractie_cylindrisch_os',
    'cycloplegische_cylindrisch_od',
    'cycloplegische_cylindrisch_os',
] as const;

type TooV1ProcedureAddEditProps = ICustomControlProcedureAddEditProps & {
    activeInsurance?: PatientInsuranceHistory | null;
    children?: JSX.Element;
    handleChangeDiagnosticReportAddEditCustomControlFields?: (
        data: Partial<ITooDiagnosticReportCustomControlData>
    ) => Promise<void>;
    handleSetHasCareGroupTOOFinancingAgreement?: (hasCareGroupTOOFinancingAgreement: boolean) => void;
    handleSetIsReferralCheckCompleted?: (isReferralCheckCompleted: boolean) => void;
    handleSetReferralCheckErrors?: Dispatch<React.SetStateAction<ValidationErrors>>;
    isSubmitted: boolean;
    referralCheckErrors?: ValidationErrors;
    requiredFields?: Partial<TooV1ProcedureRequiredFields>;
};

export function TooV1ProcedureAddEdit(props: TooV1ProcedureAddEditProps) {
    const {
        activeInsurance,
        handleChange,
        handleChangeDiagnosticReportAddEditCustomControlFields,
        isAutoSaveActive,
        procedure,
        request,
        requiredFields,
        service,
    } = props;

    const {
        customControlData,
        draftCareProviderOrganizationId,
        draftCareProviderPractitionerId,
        id: procedureId,
    } = procedure;

    const { protocol } = customControlData;
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [localReferralCheckErrors, setLocalReferralCheckErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [hasCareGroupTOOFinancingAgreement, setHasCareGroupTOOFinancingAgreement] = useState<boolean>(false);

    const referralCheckErrors = props.referralCheckErrors ?? localReferralCheckErrors;
    const setReferralCheckErrors = props.handleSetReferralCheckErrors ?? setLocalReferralCheckErrors;

    const isDirectProcedureAdded = Boolean(request.isDirectProcedureAdded);
    const isReferralCheckRequired = checkIsReferralCheckRequired({
        activeInsurance: activeInsurance ?? null,
        isDirectProcedureAdded,
    });

    const isGeneralPractitionerInvolvementRequired = checkIsGeneralPractitionerInvolvementRequired({
        isDirectProcedureAdded,
        isReferralCheckRequired,
        organizationId: draftCareProviderOrganizationId,
        practitionerId: draftCareProviderPractitionerId,
    });

    const errors = referralCheckErrors.merge(props.errors);

    useEffect(() => {
        initializeComponent(procedureId);
    }, [procedureId]);

    useEffect(() => {
        loadHasCareGroupTOOFinancingAgreement(
            request.patientId,
            draftCareProviderOrganizationId,
            draftCareProviderPractitionerId
        );
    }, [request.patientId, draftCareProviderOrganizationId, draftCareProviderPractitionerId]);

    const isReferralCheckCompleted = getIsReferralCheckCompleted({
        customControlData,
        hasCareGroupTOOFinancingAgreement,
        isDirectProcedureAdded,
        isReferralCheckRequired,
    });

    // inform parent component
    useEffect(() => {
        props.handleSetIsReferralCheckCompleted?.(isReferralCheckCompleted);
    }, [isReferralCheckCompleted]);

    // inform parent component
    useEffect(() => {
        props.handleSetHasCareGroupTOOFinancingAgreement?.(hasCareGroupTOOFinancingAgreement);
    }, [hasCareGroupTOOFinancingAgreement]);

    // Remove errors when the user selects a new care provider
    useEffect(() => {
        if (draftCareProviderOrganizationId && draftCareProviderPractitionerId) {
            setReferralCheckErrors((prevErrors) =>
                prevErrors
                    .remove('draftCareProviderOrganizationId')
                    .remove('draftCareProviderPractitionerId')
                    .remove('huisarts_is_betrokken')
            );
        }
    }, [draftCareProviderOrganizationId, draftCareProviderPractitionerId]);

    const buttons = [
        <CancelButton
            cancel={props.onCancel}
            entity={ProcessEntity.procedure}
            isExisting={!!request.id}
            key="cancel"
            revertChanges={props.revertProcedureChanges}
            serviceCode={props.service.code}
            serviceType={props.service.serviceType}
        />,
        <SaveButton
            buttonDisabled={isButtonDisabled}
            key="saveDraft"
            save={async () => {
                await saveProcedure('draft');
            }}
        />,
    ];

    if (!props.service || !props.service.isStandaloneGraderNotAllowed) {
        buttons.push(
            <SaveSendButton
                buttonDisabled={isButtonDisabled}
                key="saveFinal"
                saveSend={async () => {
                    await saveProcedure('final');
                }}
            />
        );
    }

    const verwijsbriefFiles = getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.verwijsbrief);
    const verwijsbriefField = (
        <FormGroup
            hasErrors={errors.has('files')}
            isRequired={isReferralCheckRequired}
            label="Verwijsbrief"
        >
            <AssetDropzoneWithActions
                assets={verwijsbriefFiles}
                assetsBaseUrl={FilesUrlBase.procedures}
                cleanDropzoneAfterCompletingFiles={isAutoSaveActive}
                metadata={{ placement: OphthalmologyFilePlacement.verwijsbrief }}
                onChange={handleChangeVerwijsbrief}
                onFileAccepted={async () => setIsButtonDisabled(true)}
                onUploading={async (value) => setIsButtonDisabled(value)}
                processActivityId={procedureId}
                service={service}
            />
            {errors.has('files') && (
                <FormErrorMessage>{getValidationErrorTranslation(errors.get('files'))}</FormErrorMessage>
            )}
        </FormGroup>
    );

    return (
        <NewOrEditProcedureProcessContainer
            currentProcedure={props.currentProcedure}
            isBioGrader={props.isBioGrader}
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
        >
            {isCustomControlComplete() && (
                <FormContextProvider
                    value={{
                        errors,
                        item: customControlData,
                        onChange: async (key, value) => await handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                        placeholderGroupName: 'procedure',
                    }}
                >
                    {(!isReferralCheckRequired || (isReferralCheckRequired && isReferralCheckCompleted)) && (
                        <PatientInsuranceOldCarepath
                            handleChangeCustomControl={handleChangeCustomControl}
                            isPatientInformed={customControlData.isPatientInformed}
                            mode={CustomControlMode.edit}
                            onCancel={props.onCancel}
                            serviceCode={ServiceCode.TOO}
                        />
                    )}
                    {isReferralCheckRequired === true ? (
                        <>
                            <Alert appearance="info">
                                {`Deze patiënt is verzekerd bij ${getUzoviRegisterItem(nonNullable(activeInsurance?.uzovi.code)).label} of bijbehorende verzekeraars.
                            Deze verzekeraar vereist voor een optometrisch onderzoek in veel gevallen een verwijzing van de huisarts.
                            Dit is afhankelijk van de zorggroep waarbij de huisarts aangesloten is. Vul daarom de huisarts in.`}
                            </Alert>
                            <CareProviderSelector
                                {...props}
                                entity={
                                    request.id
                                        ? procedure
                                        : {
                                              draftCareProviderOrganizationId: null,
                                              draftCareProviderPractitionerId: null,
                                          }
                                }
                                handleChange={handleChange}
                            />
                            {request.id &&
                                draftCareProviderOrganizationId &&
                                draftCareProviderPractitionerId &&
                                (isReferralCheckCompleted
                                    ? verwijsbriefFiles.length > 0
                                    : verwijsbriefFiles.length === 0) && (
                                    <>
                                        <BooleanRadioGroupFormGroupField
                                            error={errors.get('verwijsbrief_beschikbaar')}
                                            fieldKey="verwijsbrief_beschikbaar"
                                            hasErrors={errors.has('verwijsbrief_beschikbaar')}
                                            isRequired={true}
                                            label="De patiënt heeft een verwijzing van de huisarts"
                                            onChange={async (value) => {
                                                await resetCustomControlData({
                                                    verwijsbrief_beschikbaar: value,
                                                });
                                                setReferralCheckErrors((prevErrors) =>
                                                    prevErrors.remove('verwijsbrief_beschikbaar')
                                                );
                                            }}
                                            type="radio"
                                        />
                                        {customControlData.verwijsbrief_beschikbaar === false && (
                                            <Alert appearance="warning">
                                                Zonder verwijzing kan dit onderzoek niet gestart worden
                                            </Alert>
                                        )}
                                        {customControlData.verwijsbrief_beschikbaar && verwijsbriefField}
                                    </>
                                )}
                        </>
                    ) : (
                        <>
                            <CareProviderSelector
                                {...props}
                                entity={procedure}
                                handleChange={handleChange}
                            />
                            {isGeneralPractitionerInvolvementRequired && (
                                <BooleanRadioGroupFormGroupField
                                    error={errors.get('huisarts_is_betrokken')}
                                    fieldKey="huisarts_is_betrokken"
                                    hasErrors={errors.has('huisarts_is_betrokken')}
                                    isRequired={true}
                                    label="Huisarts is betrokken"
                                    onChange={async (value) => {
                                        if (value === true) {
                                            await handleChangeCustomControl({ huisarts_is_betrokken: value });
                                        } else {
                                            await handleChangeCustomControl({
                                                files: [],
                                                huisarts_betrokkenheid: null,
                                                huisarts_is_betrokken: value,
                                            });
                                        }
                                        setReferralCheckErrors((prevErrors) =>
                                            prevErrors.remove('huisarts_is_betrokken')
                                        );

                                        await props.validateOneCustomControl('huisarts_is_betrokken');
                                    }}
                                    tooltipText="Betrokkenheid betekent een actieve rol in het proces, bijvoorbeeld door afspraken met de huisarts of een mondelinge of schriftelijke verwijzing."
                                    type="radio"
                                />
                            )}

                            {customControlData.huisarts_is_betrokken && (
                                <>
                                    <FormGroup
                                        hasErrors={errors.has('huisarts_betrokkenheid')}
                                        isRequired={true}
                                        label="Betrokkenheid van de huisarts"
                                    >
                                        <RadioGroupWithTextInput
                                            error={getValidationErrorTranslation(errors.get('huisarts_betrokkenheid'))}
                                            onChange={handleChangeHuisartsBetrokkenheid}
                                            options={createRadioGroupOptions(
                                                huisartsBetrokkenheidOptions,
                                                'Anders, namelijk...'
                                            )}
                                            value={customControlData.huisarts_betrokkenheid}
                                        />
                                    </FormGroup>
                                    {customControlData.huisarts_betrokkenheid ===
                                        huisartsBetrokkenheid.schriftelijkeVerwijzing && verwijsbriefField}
                                </>
                            )}
                        </>
                    )}
                    {isReferralCheckCompleted && (
                        <>
                            {props.children}

                            <ServiceOrganizationConsumerSelector {...props} />

                            <Divider
                                color="minimal"
                                spacing="medium"
                            />

                            <HeaderDivider text="Protocol" />
                            <FormGroup
                                hasErrors={errors.has('customControlData.protocol')}
                                isRequired
                                label="Protocol"
                            >
                                <SearchableEnumDropdown
                                    automation="protocol"
                                    error={errors.get('customControlData.protocol')}
                                    jsEnum={TooProtocol}
                                    onChange={createOnChange('protocol')}
                                    onDoValidate={async () => {
                                        await props.validateOneCustomControl('protocol');
                                    }}
                                    placeholder="Kies uit de lijst"
                                    value={customControlData.protocol}
                                />
                            </FormGroup>

                            <Divider
                                color="minimal"
                                spacing="medium"
                            />
                            <HeaderDivider text="Anamnese" />

                            <FormGroup
                                hasErrors={errors.has('customControlData.reden_van_bezoek')}
                                isRequired
                                label="Reden van bezoek"
                            >
                                <SearchableEnumDropdown
                                    automation="redenVanBezoek"
                                    error={errors.get('customControlData.reden_van_bezoek')}
                                    jsEnum={TooRedenBezoek}
                                    onChange={async (value) => {
                                        if (
                                            value !== TooRedenBezoek.anders &&
                                            customControlData.reden_van_bezoek_overig
                                        ) {
                                            await handleChangeCustomControl({ reden_van_bezoek_overig: null });
                                        }
                                        await createOnChange('reden_van_bezoek')(value);
                                    }}
                                    onDoValidate={async () => {
                                        await props.validateOneCustomControl('reden_van_bezoek');
                                    }}
                                    placeholder="Kies uit de lijst"
                                    value={customControlData.reden_van_bezoek}
                                />
                            </FormGroup>

                            {customControlData.reden_van_bezoek === TooRedenBezoek.anders && (
                                <TextAreaField
                                    error={errors.get('customControlData.reden_van_bezoek_overig')}
                                    fieldKey="reden_van_bezoek_overig"
                                    hasErrors={errors.has('customControlData.reden_van_bezoek_overig')}
                                    isRequired
                                    label="Andere reden"
                                    onChange={createOnChange('reden_van_bezoek_overig')}
                                    onDoValidate={async () => {
                                        await props.validateOneCustomControl('reden_van_bezoek_overig');
                                    }}
                                    placeholder="Bijvoorbeeld patiënt met een verhoogd risico op een oogziekte"
                                    value={customControlData.reden_van_bezoek_overig}
                                />
                            )}
                            <TextAreaField
                                error={errors.get('customControlData.klachten')}
                                fieldKey="klachten"
                                hasErrors={errors.has('customControlData.klachten')}
                                label="Klachten"
                                onChange={createOnChange('klachten')}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('klachten');
                                }}
                                placeholder="Bijvoorbeeld hoofdpijn, wazig of dubbel zien, moeite met scherpstellen..."
                                value={customControlData.klachten}
                            />

                            <TextAreaField
                                automation="oculaireVoorgeschiedenis"
                                error={errors.get('customControlData.oculaire_voorgeschiedenis')}
                                fieldKey="oculaire_voorgeschiedenis"
                                hasErrors={errors.has('customControlData.oculaire_voorgeschiedenis')}
                                isRequired
                                label="Oculaire voorgeschiedenis"
                                onChange={createOnChange('oculaire_voorgeschiedenis')}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('oculaire_voorgeschiedenis');
                                }}
                                placeholder="Bijvoorbeeld bezoek oogarts, lenzen, bril..."
                                value={customControlData.oculaire_voorgeschiedenis}
                            />

                            <TextAreaField
                                error={errors.get('customControlData.familiaire_voorgeschiedenis')}
                                fieldKey="familiaire_voorgeschiedenis"
                                hasErrors={errors.has('customControlData.familiaire_voorgeschiedenis')}
                                label="Familiaire voorgeschiedenis"
                                onChange={createOnChange('familiaire_voorgeschiedenis')}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('familiaire_voorgeschiedenis');
                                }}
                                placeholder="Oogziekten in de familie..."
                                value={customControlData.familiaire_voorgeschiedenis}
                            />
                            <TextAreaField
                                error={errors.get('customControlData.algemene_gezondheid_en_medicatie')}
                                fieldKey="algemene_gezondheid_en_medicatie"
                                hasErrors={errors.has('customControlData.algemene_gezondheid_en_medicatie')}
                                label="Algemene gezondheid en medicatie"
                                onChange={createOnChange('algemene_gezondheid_en_medicatie')}
                                onDoValidate={async () => {
                                    await props.validateOneCustomControl('algemene_gezondheid_en_medicatie');
                                }}
                                placeholder="Gezondheid, levensstijl en medicijngebruik..."
                                value={customControlData.algemene_gezondheid_en_medicatie}
                            />

                            <TriStateField
                                error={errors.get('customControlData.rijdt_auto')}
                                fieldKey="rijdt_auto"
                                hasErrors={errors.has('customControlData.rijdt_auto')}
                                isRequired={
                                    protocol === TooProtocol.cataract || (requiredFields && requiredFields.rijdt_auto)
                                }
                                label="Rijdt auto"
                                value={customControlData.rijdt_auto}
                            />

                            <TriStateField
                                error={errors.get('customControlData.ervaart_klachten_als_storend')}
                                fieldKey="ervaart_klachten_als_storend"
                                hasErrors={errors.has('customControlData.ervaart_klachten_als_storend')}
                                isRequired={
                                    protocol === TooProtocol.cataract ||
                                    (requiredFields && requiredFields.ervaart_klachten_als_storend)
                                }
                                label="Ervaart klachten als storend"
                                value={customControlData.ervaart_klachten_als_storend}
                            />

                            <Divider
                                color="minimal"
                                spacing="medium"
                            />
                            <HeaderDivider text={getSelectedLanguage().diagnosticReport.examination} />

                            <TriStateField
                                automation="draagtCorrectie"
                                error={errors.get('customControlData.draagt_correctie')}
                                fieldKey="draagt_correctie"
                                hasErrors={errors.has('customControlData.draagt_correctie')}
                                isRequired={true}
                                label="Draagt correctie"
                                value={customControlData.draagt_correctie}
                            />

                            <FormGroup
                                hasErrors={
                                    errors.has('customControlData.visus_od') || errors.has('customControlData.visus_os')
                                }
                                isRequired
                                label="Visus zonder of eigen correctie"
                            >
                                <Row columnGap={[2, 4, 6]}>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OD" />
                                            <SmallNullableInputText
                                                automation="visusOd"
                                                error={errors.get('customControlData.visus_od')}
                                                onChange={createOnChange('visus_od')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('visus_od')
                                                }
                                                value={customControlData.visus_od}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OS" />
                                            <SmallNullableInputText
                                                automation="visusOs"
                                                error={errors.get('customControlData.visus_os')}
                                                onChange={createOnChange('visus_os')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('visus_os')
                                                }
                                                value={customControlData.visus_os}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hasErrors={
                                    errors.has('customControlData.refractie_sferisch_od') ||
                                    errors.has('customControlData.refractie_sferisch_os') ||
                                    errors.has('customControlData.refractie_cylindrish_od') ||
                                    errors.has('customControlData.refractie_cylindrish_os') ||
                                    errors.has('customControlData.refractie_as_od') ||
                                    errors.has('customControlData.refractie_as_os')
                                }
                                isRequired
                                label="Refractie"
                            >
                                <Row columnGap={[2, 4, 6]}>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OD" />
                                            <Row columnGap={2}>
                                                <Col span={4}>
                                                    <InputText
                                                        automation="refractieSferischOd"
                                                        error={errors.get('customControlData.refractie_sferisch_od')}
                                                        onChange={createOnChange('refractie_sferisch_od')}
                                                        onDoValidate={async () =>
                                                            await props.validateOneCustomControl(
                                                                'refractie_sferisch_od'
                                                            )
                                                        }
                                                        placeholder="sph"
                                                        value={customControlData.refractie_sferisch_od || ''}
                                                    />
                                                </Col>
                                                <Col span={4}>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            mr={1}
                                                            size="sm"
                                                        >
                                                            -
                                                        </Text>
                                                        <InputText
                                                            automation="refractieCylindrishOd"
                                                            error={errors.get(
                                                                'customControlData.refractie_cylindrish_od'
                                                            )}
                                                            onChange={createOnChange('refractie_cylindrish_od')}
                                                            onDoValidate={async () =>
                                                                await props.validateOneCustomControl(
                                                                    'refractie_cylindrish_od'
                                                                )
                                                            }
                                                            placeholder="cyl"
                                                            value={customControlData.refractie_cylindrish_od || ''}
                                                        />
                                                    </Box>
                                                </Col>
                                                <Col span={4}>
                                                    <InputText
                                                        automation="refractieAsOd"
                                                        error={errors.get('customControlData.refractie_as_od')}
                                                        onChange={createOnChange('refractie_as_od')}
                                                        onDoValidate={async () =>
                                                            await props.validateOneCustomControl('refractie_as_od')
                                                        }
                                                        placeholder="ax"
                                                        value={customControlData.refractie_as_od || ''}
                                                    />
                                                </Col>
                                            </Row>
                                        </StyledBorderAndPadding>
                                    </Col>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OS" />
                                            <Row columnGap={2}>
                                                <Col span={4}>
                                                    <InputText
                                                        automation="refractieSferischOs"
                                                        error={errors.get('customControlData.refractie_sferisch_os')}
                                                        onChange={createOnChange('refractie_sferisch_os')}
                                                        onDoValidate={async () =>
                                                            await props.validateOneCustomControl(
                                                                'refractie_sferisch_os'
                                                            )
                                                        }
                                                        placeholder="sph"
                                                        value={customControlData.refractie_sferisch_os || ''}
                                                    />
                                                </Col>
                                                <Col span={4}>
                                                    <Box
                                                        alignItems="center"
                                                        display="flex"
                                                    >
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            mr={1}
                                                            size="sm"
                                                        >
                                                            -
                                                        </Text>
                                                        <InputText
                                                            automation="refractieCylindrishOs"
                                                            error={errors.get(
                                                                'customControlData.refractie_cylindrish_os'
                                                            )}
                                                            onChange={createOnChange('refractie_cylindrish_os')}
                                                            onDoValidate={async () =>
                                                                await props.validateOneCustomControl(
                                                                    'refractie_cylindrish_os'
                                                                )
                                                            }
                                                            placeholder="cyl"
                                                            value={customControlData.refractie_cylindrish_os || ''}
                                                        />
                                                    </Box>
                                                </Col>
                                                <Col span={4}>
                                                    <InputText
                                                        automation="refractieAsOs"
                                                        error={errors.get('customControlData.refractie_as_os')}
                                                        onChange={createOnChange('refractie_as_os')}
                                                        onDoValidate={async () =>
                                                            await props.validateOneCustomControl('refractie_as_os')
                                                        }
                                                        placeholder="ax"
                                                        value={customControlData.refractie_as_os || ''}
                                                    />
                                                </Col>
                                            </Row>
                                        </StyledBorderAndPadding>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hasErrors={
                                    errors.has('customControlData.visus_refractie_od') ||
                                    errors.has('customControlData.visus_refractie_os')
                                }
                                isRequired
                                label="Visus na refractie"
                            >
                                <Row columnGap={[2, 4, 6]}>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OD" />
                                            <SmallNullableInputText
                                                automation="visusRefractieOd"
                                                error={errors.get('customControlData.visus_refractie_od')}
                                                onChange={createOnChange('visus_refractie_od')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('visus_refractie_od')
                                                }
                                                value={customControlData.visus_refractie_od}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OS" />
                                            <SmallNullableInputText
                                                automation="visusRefractieOs"
                                                error={errors.get('customControlData.visus_refractie_os')}
                                                onChange={createOnChange('visus_refractie_os')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('visus_refractie_os')
                                                }
                                                value={customControlData.visus_refractie_os}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hasErrors={
                                    errors.has('customControlData.stenopeische_visus_od') ||
                                    errors.has('customControlData.stenopeische_visus_os')
                                }
                                isRequired
                                label="Stenopeïsche visus"
                            >
                                <Row columnGap={[2, 4, 6]}>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OD" />
                                            <SmallNullableInputText
                                                automation="stenopeischeVisusOd"
                                                error={errors.get('customControlData.stenopeische_visus_od')}
                                                onChange={createOnChange('stenopeische_visus_od')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('stenopeische_visus_od')
                                                }
                                                value={customControlData.stenopeische_visus_od}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OS" />
                                            <SmallNullableInputText
                                                automation="stenopeischeVisusOs"
                                                error={errors.get('customControlData.stenopeische_visus_os')}
                                                onChange={createOnChange('stenopeische_visus_os')}
                                                onDoValidate={async () =>
                                                    await props.validateOneCustomControl('stenopeische_visus_os')
                                                }
                                                value={customControlData.stenopeische_visus_os}
                                            />
                                        </StyledBorderAndPadding>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hasErrors={
                                    errors.has('customControlData.oogdruk_od') ||
                                    errors.has('customControlData.oogdruk_os')
                                }
                                isRequired
                                label="Oogdruk (applanatie tonometrie)"
                            >
                                <Row columnGap={[2, 4, 6]}>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OD" />
                                            <Box
                                                alignItems="center"
                                                display="flex"
                                            >
                                                <SmallNullableInputText
                                                    automation="oogdrukOd"
                                                    error={errors.get('customControlData.oogdruk_od')}
                                                    onChange={createOnChange('oogdruk_od')}
                                                    onDoValidate={async () =>
                                                        await props.validateOneCustomControl('oogdruk_od')
                                                    }
                                                    value={customControlData.oogdruk_od}
                                                />
                                                <Text
                                                    as="span"
                                                    fontWeight="bold"
                                                    ml={1}
                                                    size="sm"
                                                >
                                                    mmHg
                                                </Text>
                                            </Box>
                                        </StyledBorderAndPadding>
                                    </Col>
                                    <Col span={[12, 6, null, null, 4]}>
                                        <StyledBorderAndPadding>
                                            <TooV1ProcedureAddEditOdOsLabel side="OS" />
                                            <Box
                                                alignItems="center"
                                                display="flex"
                                            >
                                                <SmallNullableInputText
                                                    automation="oogdrukOs"
                                                    error={errors.get('customControlData.oogdruk_os')}
                                                    onChange={createOnChange('oogdruk_os')}
                                                    onDoValidate={async () =>
                                                        await props.validateOneCustomControl('oogdruk_os')
                                                    }
                                                    value={customControlData.oogdruk_os}
                                                />
                                                <Text
                                                    as="span"
                                                    fontWeight="bold"
                                                    ml={1}
                                                    size="sm"
                                                >
                                                    mmHg
                                                </Text>
                                            </Box>
                                        </StyledBorderAndPadding>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <TooExtraExaminationSelector
                                customControlData={customControlData}
                                errors={renameErrorKeys(errors, 'customControlData', 'customControlData')}
                                handleChange={async (key, value) => await props.handleChange(key, value)}
                                handleChangeCustomControl={handleChangeCustomControl}
                                isSubmitted={props.isSubmitted}
                                procedure={procedure}
                                protocol={protocol}
                                requiredFields={requiredFields}
                                validateOne={async (name) => await props.validateOne(name)}
                                validateOneCustomControl={async (name, index) =>
                                    await props.validateOneCustomControl(name, index)
                                }
                            ></TooExtraExaminationSelector>

                            <Divider
                                color="minimal"
                                spacing="medium"
                            />

                            <HeaderDivider text="Foto's (fundus, OCT, etc.)" />

                            <OphthalmologyDropzoneProcedureAddEdit
                                assets={customControlData.files as IOphthalmologyAsset[]}
                                handleChangeCustomControl={handleChangeCustomControl}
                                isAutoSaveActive={props.isAutoSaveActive}
                                onUploading={async (isUploading) => setIsButtonDisabled(isUploading)}
                                procedure={procedure}
                                service={props.service}
                            />

                            {(protocol === TooProtocol.basisonderzoek || protocol === TooProtocol.cataract) && (
                                <Alert appearance="warning">
                                    Let op: voor het {protocol.toLowerCase()} protocol is een centrale fundusfoto
                                    verplicht
                                </Alert>
                            )}

                            {(protocol === TooProtocol.glaucoom || protocol === TooProtocol.maculaDegeneratie) && (
                                <Alert appearance="warning">
                                    Let op: Voor het {protocol.toLowerCase()} protocol is een centrale fundusfoto en OCT
                                    verplicht
                                </Alert>
                            )}
                        </>
                    )}
                </FormContextProvider>
            )}

            <ButtonContainer
                alignRight
                buttons={buttons}
                hideButtons={props.isBioGrader}
            />
        </NewOrEditProcedureProcessContainer>
    );

    async function initializeComponent(id: string | undefined) {
        if (id) {
            const updatedCylindrishValues = removeMinusCylindrishValues(customControlData);
            await handleChangeCustomControl(updatedCylindrishValues);
        }
    }

    async function loadHasCareGroupTOOFinancingAgreement(
        patientId: string,
        organizationId: string | null,
        practitionerId: string | null
    ) {
        const { careGroupHasTOOFinancingAgreement } = await requestServiceConditionals(
            patientId,
            organizationId,
            practitionerId
        );
        setHasCareGroupTOOFinancingAgreement(careGroupHasTOOFinancingAgreement);
    }

    function isCustomControlComplete() {
        const emptyCustomControl = tooV1ProcedureInitializeCustomControlData();
        for (const key of Object.keys(emptyCustomControl)) {
            if (customControlData[key as keyof ITooProcedureCustomControlData] === undefined) {
                return false;
            }
        }

        return true;
    }

    /**
     * Same function as props, but with better typing (props uses `any`)
     */
    async function handleChangeCustomControl(values: Partial<ITooProcedureCustomControlData>): Promise<void> {
        await props.handleChangeCustomControl<ITooProcedureCustomControlData>(values);
    }

    async function handleChangeVerwijsbrief(assets: IAsset[]): Promise<void> {
        const files: IOphthalmologyAsset[] = assets.map((asset) => ({
            ...asset,
            placement: OphthalmologyFilePlacement.verwijsbrief,
        }));

        if (files.length === 0 && isReferralCheckRequired) {
            await resetCustomControlData({
                verwijsbrief_beschikbaar: customControlData.verwijsbrief_beschikbaar,
            });
        } else {
            await handleChangeCustomControl({
                files: [
                    ...files,
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.od),
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.os),
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.aanvullend),
                ],
            });
            setReferralCheckErrors((prevErrors) => prevErrors.remove('files'));

            await props.validateOneCustomControl('files');
        }
    }

    async function resetCustomControlData(data: Partial<ITooProcedureCustomControlData> = {}): Promise<void> {
        await handleChangeCustomControl({
            ...tooV1InitializeCustomControlData(),
            ...tooV1ProcedureInitializeCustomControlData(),
            ...data,
        });

        handleChangeDiagnosticReportAddEditCustomControlFields?.(tooV1DiagnosticReportInitializeCustomControlData());
    }

    function createOnChange(key: string) {
        return async (value: string | null) => {
            await handleChangeCustomControl({ [key]: value && value.trimStart() });
        };
    }

    async function saveProcedure(saveType: 'draft' | 'final') {
        if (isButtonDisabled) {
            return;
        }
        setIsButtonDisabled(true);

        const updatedCylindrishValues = addMinusCylindrishValues(customControlData);
        await handleChangeCustomControl(updatedCylindrishValues);

        const validatedProcedure = await props.validateProcedure(saveType);
        const validateResult = validateReferralCheck({
            hasCareGroupTOOFinancingAgreement,
            isDirectProcedureAdded,
            isReferralCheckRequired,
            procedure,
        });
        setReferralCheckErrors(validateResult.errors);

        if (!isReferralCheckCompleted || !validatedProcedure || validateResult.hasErrors) {
            const newCylindrishValues = removeMinusCylindrishValues(customControlData);
            await handleChangeCustomControl(newCylindrishValues);
            setIsButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveProcedure(saveType);
        setIsButtonDisabled(false);
        if (!requestId) {
            // Should the cilindrish values be reverted here as well?
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                props.service,
                'procedure',
                getSelectedLanguage().toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                getSelectedLanguage().toast.draftDefaultMessage
            );
        }

        await props.onSave(requestId);
    }

    async function handleChangeHuisartsBetrokkenheid(value: string) {
        if (!value) {
            await resetCustomControlData({
                huisarts_betrokkenheid: '',
                huisarts_is_betrokken: customControlData.huisarts_is_betrokken,
            });
        } else if (value !== huisartsBetrokkenheid.schriftelijkeVerwijzing) {
            await handleChangeCustomControl({
                files: [
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.od),
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.os),
                    ...getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.aanvullend),
                ],
                huisarts_betrokkenheid: value,
            });
        } else {
            await handleChangeCustomControl({
                huisarts_betrokkenheid: value,
            });
        }
        setReferralCheckErrors((prevErrors) => prevErrors.remove('huisarts_betrokkenheid'));
        await props.validateOneCustomControl('huisarts_betrokkenheid');
    }
}

export function removeMinusCylindrishValues(customControlData: ITooProcedureCustomControlData) {
    const updatedCylindrishValues: Record<string, unknown> = {};

    for (const key of tooCylindrishFields) {
        const value = customControlData[key];
        if (value && value.startsWith('-')) {
            updatedCylindrishValues[key] = value.replace(/-+/, '');
        }
    }

    return updatedCylindrishValues;
}

export function addMinusCylindrishValues(customControlData: ITooProcedureCustomControlData) {
    const updatedCylindrishValues: Record<string, unknown> = {};

    for (const key of tooCylindrishFields) {
        const value = customControlData[key];
        if (value && !value.startsWith('-')) {
            updatedCylindrishValues[key] = `-${value}`;
        }
    }

    return updatedCylindrishValues;
}
